import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import numeral from 'numeral';

import EllipsisMenu  from 'app/components/common/ellipsis-menu';
import Icon          from 'app/components/common/icon';
import Modal         from 'app/components/common/modal';
import SortableList  from 'app/components/common/sortable-list';
import StandardInput from 'app/components/common/standard-input';
import GrantSysDuck  from 'app/ducks/company-admin/grant-system';
import format        from 'app/helpers/format';
import helpers       from 'app/helpers/grant-system';
import utils         from 'app/helpers/utils';
import prompts       from 'app/prompts';
import RoutingSlx    from 'app/selectors/routing';

const {StageTypes} = helpers;



class CadminModalManageStages extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      stages: props.workflow.stages.map(s => _.pick(s, ['id', 'title', 'type', 'color'])),
      editIndex: -1,
      deleteIds: [],
      savePending: false,
      saveFailed: false,
      appCounts: null,
    };

    this.onKeyPressTitle = this.onKeyPressTitle.bind(this);
    this.onBlurTitle = this.onBlurTitle.bind(this);
    this.onSort = this.onSort.bind(this);
    this.onClickSave = this.onClickSave.bind(this);
    this.renderStage = this.renderStage.bind(this);
    this.onClickNewStage = this.onClickNewStage.bind(this);
  }

  get systemStages() {
    return this.props.workflow.stages.filter(s => s.type !== StageTypes.USER_DEFINED);
  }

  get canSave() {
    if (this.state.savePending) return false;
    if (!this.state.stages.length) return false;
    return this.state.stages.every(s => !!s.title.trim());
  }

  get hasApps() {
    return !!this.props.appCounts[this.props.workflow.id];
  }

  setColors(stages) {
    const colors = helpers.colors(stages.length);
    return stages.map((stage, i) => {
      return {...stage, color: colors[i]};
    });
  }

  onSort(stages) {
    if (!helpers.hasValidSystemStages(stages)) return;
    this.setState({stages: this.setColors(stages)});
  }

  onKeyPressTitle(event) {
    const isEnter = event.key === 'Enter';
    if (isEnter) {
      this.setState({editIndex: -1});
    }
  }

  onBlurTitle() {
    this.setState({editIndex: -1});
  }

  onChangeTitle(stage, index, event) {
    const title = event.target.value;
    this.setState((prevState) => {
      const stages = [...prevState.stages];
      const stage = {...stages[index], title};
      stages[index] = stage;
      return {stages: this.setColors(stages)};
    });
  }

  onClickEdit(index, event) {
    if (event) event.preventDefault();
    this.setState({editIndex: index});
  }

  onClickNewStage() {
    let index;
    this.setState((prevState) => {
      const stages = [...prevState.stages];
      index = stages.length - 3;
      stages.splice(index, 0, {id: utils.clientId(), title: 'New Stage', type: StageTypes.USER_DEFINED});
      return {stages: this.setColors(stages)};
    }, () => {
      this.onClickEdit(index);
    });
  }

  async onClickDelete(stage, index, event) {
    if (event) event.preventDefault();
    const isNew = utils.isClientId(stage.id);
    this.setState((prevState) => {
      const stages = prevState.stages.filter(s => s.id !== stage.id);
      return {
        stages: this.setColors(stages),
        deleteIds: isNew ? prevState.deleteIds : [...prevState.deleteIds, stage.id],
      };
    });
  }

  async onClickSave() {
    if (!this.canSave) return;
    if (this.hasApps) {
      const didConfirm = await prompts.confirm({msg: 'This workflow currently has applications in it.\nAre you sure you want to change the stages?', doubleConfirm: true, confirmBtnLabel: 'Save Changes to Stages'});
      if (!didConfirm) return;
    }

    this.setState({savePending: true, saveFailed: false});
    const {stages, deleteIds} = this.state;
    try {
      await this.props.save(stages, deleteIds);
    } catch (error) {
      this.setState({savePending: false, saveFailed: true});
      throw error;
    }
    this.setState({savePending: false});
    this.props.onClose();
  }

  renderStage(stage, index, dnd) {
    const isSystem = stage.type !== StageTypes.USER_DEFINED;
    const isEdit = this.state.editIndex === index;
    const typeClass = isSystem ? 'type-system' : 'type-user';
    const appCount = this.props.appCounts[stage.id] || 0;
    const canDelete = !isSystem && appCount === 0;

    return (
      <div className={`manage-stages-stage ${dnd.className} ${typeClass}`}>
        {!isSystem && dnd.handleEl}
        {isEdit ? (<>
          <StandardInput name="title" label="Stage Title" className="manage-stages-stage-title-input" onChange={this.onChangeTitle.bind(this, stage, index)} onKeyPress={this.onKeyPressTitle} onBlur={this.onBlurTitle} value={stage.title} focusOnMount />
        </>) : (<>
          <div className="manage-stages-stage-title" onClick={this.onClickEdit.bind(this, index)} style={{backgroundColor: stage.color}}>{stage.title}</div>
          {this.hasApps && (
            <div className="manage-stages-stage-subtext">{`${numeral(appCount).format('0,0')} ${format.pluralize('app', appCount)}`}</div>
          )}
          <div className="manage-stages-stage-spacer" />
        </>)}
        <EllipsisMenu className="alt1 manage-stages-stage-menu" usePortal>
          <button onClick={this.onClickEdit.bind(this, index)}><Icon.Pencil /> Change Title</button>
          {!isSystem && (
            <button onClick={this.onClickDelete.bind(this, stage, index)} disabled={!canDelete}><Icon.Bin /> Delete</button>
          )}
        </EllipsisMenu>
      </div>
    );
  }

  render() {
    // const {stages, savePending, saveFailed, deleteIds} = this.state;
    const {stages, savePending, saveFailed} = this.state;
    // const renderableStages = stages.filter(s => !deleteIds.includes(s.id));
    const canAddNew = stages.length < helpers.MAX_STAGE_COUNT;

    return (
      <Modal className="bform manage-stages" onClose={this.props.onClose}>
        <h1 className="bform-h1">Manage Stages</h1>
        {this.hasApps && (
          <p className="notice">Stages that currently have applications in them can not be deleted.</p>
        )}

        <div className="manage-stages-stages _mt-24">
          <SortableList items={stages} renderItem={this.renderStage} onSort={this.onSort} distinctHandle forceUpdate={this.state.editIndex} />
        </div>
        <div className="_mt-12">
          <button className="btn small secondary icon ghost" onClick={this.onClickNewStage} disabled={!canAddNew}><Icon.Add /> Add New Stage</button>
        </div>

        <div className="bform-actions">
          <div className="bform-actions-right">
            <button className="btn slate secondary ghost" onClick={this.props.onClose}>Cancel</button>
            <button className="btn blue" onClick={this.onClickSave} disabled={!this.canSave}>{savePending ? 'Saving...' : 'Save'}</button>
          </div>
        </div>

        {saveFailed && (
          <p className="bform-error">Oops! Something went wrong.</p>
        )}
      </Modal>
    );
  }

}

CadminModalManageStages.propTypes = {
  workflow: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

const stateToProps = (state) => ({
  companySlug: RoutingSlx.params(state).companySlug,
  appCounts: GrantSysDuck.Slx.appCounts(state),
});
const dispatchToProps = (dispatch, ownProps) => ({
  save: (stages, deleteIds) => dispatch(GrantSysDuck.Ax.workflowsUpdateStages(null, ownProps.workflow.id, {stages, deleteIds})),
});

export default connect(stateToProps, dispatchToProps)(CadminModalManageStages);
